import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";

import { FormPageHeading } from "@/shared/components";
import { OrgRoleOnly } from "@/shared/components/OrgRoleOnly";
import {
  ONLY_ADMINS_CHANGE_RULES,
  rulePrefillFromSearchParams,
} from "@/shared/domain/ingestionRules";
import { useCreateIngestionRule } from "@/shared/domain/ingestionRules/hooks";
import {
  useTriggerById,
  useUpdateTrigger,
} from "@/shared/domain/triggers/hooks";
import { Banner, ConfirmDialog } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { type RuleSavedState } from "./IngestionRuleDetail";
import { IngestionRuleForm } from "./IngestionRuleForm";
import { TriggerExclusionsNote } from "./IngestionRuleNudge";
import styles from "./IngestionRulePages.module.css";
import { IngestionRulesChrome } from "./IngestionRulesChrome";

/**
 * Write a new ingestion rule and land on its page. The URL may prefill it (see
 * `Goto.createNewIngestionRuleRoute`); a rule converted from a trigger then offers
 * to disable that trigger, which would otherwise ingest the same files again,
 * or, when the trigger could not be loaded, says to check it.
 * Only org admins see the form.
 */
export function CreateIngestionRule() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Read once: the form's defaults are fixed when it mounts.
  const [prefill] = React.useState(() =>
    rulePrefillFromSearchParams(searchParams),
  );
  const { mutateAsync: createRule } = useCreateIngestionRule();
  const sourceTriggerId = prefill?.sourceTriggerId;
  const sourceTrigger = useTriggerById(sourceTriggerId);
  const updateTrigger = useUpdateTrigger();
  const [createdRuleId, setCreatedRuleId] = React.useState<string | null>(null);
  const [disableError, setDisableError] = React.useState<unknown>(null);

  const sourceTriggerRecord = sourceTrigger.data;
  const goToRule = (ruleId: string) =>
    void navigate(Goto.createIngestionRuleRoute(ruleId), {
      state: { ruleSaved: true } satisfies RuleSavedState,
    });

  return (
    <IngestionRulesChrome title="New rule">
      <div className={styles.page}>
        <FormPageHeading
          title="New ingestion rule"
          subtitle="Applies to files uploaded after you save."
        />
        <OrgRoleOnly
          role="admin"
          fallback={<Banner>{ONLY_ADMINS_CHANGE_RULES}</Banner>}
        >
          {prefill?.triggerExclusions !== undefined && (
            <TriggerExclusionsNote patterns={prefill.triggerExclusions} />
          )}
          <IngestionRuleForm
            mode="create"
            prefill={prefill}
            onSubmit={async (request) => {
              const rule = await createRule(request);
              if (sourceTriggerId === undefined) {
                goToRule(rule.rule_id);
                return;
              }
              // Waits for a trigger still loading; undefined if it failed to load.
              const { data: trigger } =
                sourceTrigger.data === undefined
                  ? await sourceTrigger.refetch()
                  : sourceTrigger;
              if (trigger?.enabled === false) {
                goToRule(rule.rule_id);
              } else {
                setCreatedRuleId(rule.rule_id);
              }
            }}
            onCancel={() => void navigate(Goto.route.Settings.Ingestion.Root)}
          />
        </OrgRoleOnly>
      </div>
      {createdRuleId !== null && sourceTriggerRecord !== undefined && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              goToRule(createdRuleId);
            }
          }}
          title={`Disable the trigger '${sourceTriggerRecord.name}'?`}
          description="Leaving it enabled ingests matching files twice."
          confirmLabel="Disable"
          cancelLabel="Keep enabled"
          pending={updateTrigger.isPending}
          onConfirm={() => {
            setDisableError(null);
            updateTrigger.mutate(
              { name: sourceTriggerRecord.name, updates: { enabled: false } },
              {
                onSuccess: () => goToRule(createdRuleId),
                onError: setDisableError,
              },
            );
          }}
        >
          {disableError !== null && (
            <Banner variant="error">
              {userFacingErrorMessage(disableError)}
            </Banner>
          )}
        </ConfirmDialog>
      )}
      {createdRuleId !== null &&
        sourceTriggerId !== undefined &&
        sourceTriggerRecord === undefined && (
          <ConfirmDialog
            open
            onOpenChange={(open) => {
              if (!open) {
                goToRule(createdRuleId);
              }
            }}
            title="Check the trigger this rule replaces"
            description="Roboto couldn’t load it, so it can’t tell whether it is still enabled. If it is, disable it, or matching files are ingested twice."
            confirmLabel="Open trigger"
            cancelLabel="Go to rule"
            onConfirm={() =>
              void navigate(Goto.createTriggerByIdRoute(sourceTriggerId))
            }
          >
            {sourceTrigger.error !== null && (
              <Banner variant="error">
                {userFacingErrorMessage(sourceTrigger.error)}
              </Banner>
            )}
          </ConfirmDialog>
        )}
    </IngestionRulesChrome>
  );
}
